/**
 * FaqItem component for displaying a single FAQ
 */
import { UserRole } from "@features/settings/types/settings.types";
import { useProfile } from "@shared/hooks/useProfile";
import React from "react";
import { Faq } from "../types/faqs.types";

interface FaqItemProps {
  faq: Faq;
  onEdit?: (faq: Faq) => void;
  onDelete?: (id: string) => void;
}

const FaqItem: React.FC<FaqItemProps> = ({ faq, onEdit, onDelete }) => {
  const { user } = useProfile();
  const isAdmin = user?.role === UserRole.ADMIN;

  return (
    <article className="py-4 border-b border-gray-200 dark:border-gray-700/60">
      <header className="flex items-start mb-2">
        <div className="mt-2 mr-3">
          <svg
            className="shrink-0 fill-current"
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path
              className="text-blue-300"
              d="M4 8H0v4.9c0 1 .7 1.9 1.7 2.1 1.2.2 2.3-.8 2.3-2V8z"
            />
            <path
              className="text-blue-500"
              d="M15 1H7c-.6 0-1 .4-1 1v11c0 .7-.2 1.4-.6 2H13c1.7 0 3-1.3 3-3V2c0-.6-.4-1-1-1z"
            />
          </svg>
        </div>
        <div className="flex-grow">
          <h3 className="text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold">
            {faq.title}
          </h3>
        </div>
        {isAdmin && (
          <div className="flex space-x-2 ml-4">
            <button
              onClick={() => onEdit && onEdit(faq)}
              className="text-gray-400 hover:text-blue-500"
              aria-label="Edit"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                ></path>
              </svg>
            </button>
            <button
              onClick={() => onDelete && onDelete(faq.id)}
              className="text-gray-400 hover:text-red-500"
              aria-label="Delete"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                ></path>
              </svg>
            </button>
          </div>
        )}
      </header>
      <div className="pl-7">
        <div
          className="mb-2"
          dangerouslySetInnerHTML={{ __html: faq.description }}
        />
        <ul className="flex flex-wrap">
          {faq.categories.map((category, index) => (
            <li
              key={`${faq.id}-category-${index}`}
              className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2"
            >
              <span className="text-sm font-medium text-blue-500">
                {category}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
};

export default FaqItem;
